import type { BlendDriver } from "../blend-reader/types/BlendDriver.ts";
import {
  DriverAggregation,
  DriverVarType,
} from "../blend-reader/types/BlendDriver.ts";
import type { BlendShapeKeySet } from "../blend-reader/types/BlendShapeKey.ts";
import { evaluateFCurve } from "./FCurveEvaluator.ts";
import { resolvePropertyPath } from "./PropertyPathResolver.ts";

/**
 * Represents a single driver node in the dependency graph.
 */
interface DriverNode {
  /** The driver definition from the .blend file */
  driver: BlendDriver;
  /** Which shape key set this driver's TARGET lives in */
  targetSetName: string;
  /** Parsed target path → shape key name */
  targetKeyName: string;
  /** Source dependencies: [setName, keyName] pairs */
  sources: Array<{ setName: string; keyName: string }>;
}

/**
 * Builds and evaluates a driver dependency graph.
 *
 * The graph connects shape key weights across objects:
 * when the user changes a slider on one mesh, drivers propagate
 * the value to shape keys on other meshes.
 *
 * Reference: docs/02-architecture.md "Driver Evaluation" section
 */
export class DriverGraph {
  private nodes: DriverNode[] = [];
  private evaluationOrder: DriverNode[] = [];

  /**
   * Build the driver graph from all shape key sets in the file.
   */
  build(shapeKeySets: Map<string, BlendShapeKeySet>): void {
    this.nodes = [];

    // Collect all drivers from all shape key sets
    for (const [setName, skSet] of shapeKeySets) {
      for (const driver of skSet.drivers) {
        // Parse target path
        const targetResolved = resolvePropertyPath(driver.targetPath);
        if (!targetResolved) continue;

        // Resolve source dependencies
        const sources: DriverNode["sources"] = [];

        for (const variable of driver.variables) {
          // Only handle SINGLE_PROP variables (LOC_DIFF etc. are orphaned in our test files)
          if (variable.type !== DriverVarType.SingleProp) continue;

          for (const target of variable.targets) {
            if (!target.rnaPath || !target.datablockName) continue;

            const sourceResolved = resolvePropertyPath(target.rnaPath);
            if (!sourceResolved) continue;

            // Find which shape key set this source belongs to
            const sourceSetName = findSetByDatablockName(
              shapeKeySets,
              target.datablockName,
            );
            if (!sourceSetName) continue;

            sources.push({
              setName: sourceSetName,
              keyName: sourceResolved.keyName,
            });
          }
        }

        // Only add nodes that have resolvable sources
        if (sources.length > 0) {
          this.nodes.push({
            driver,
            targetSetName: setName,
            targetKeyName: targetResolved.keyName,
            sources,
          });
        }
      }
    }

    // Topological sort
    this.evaluationOrder = topologicalSort(this.nodes);
  }

  /**
   * Evaluate all drivers in topological order.
   * Called whenever a shape key weight changes.
   *
   * @param weights - Mutable weight map: setName → keyName → weight
   * @returns Set of [setName, keyName] pairs that were modified by drivers
   */
  evaluate(
    weights: Map<string, Map<string, number>>,
  ): Array<{ setName: string; keyName: string; value: number }> {
    const changes: Array<{ setName: string; keyName: string; value: number }> =
      [];

    for (const node of this.evaluationOrder) {
      const { driver, targetSetName, targetKeyName, sources } = node;

      // Read source values
      const inputValues: number[] = [];
      for (const src of sources) {
        const setWeights = weights.get(src.setName);
        inputValues.push(setWeights?.get(src.keyName) ?? 0);
      }

      if (inputValues.length === 0) continue;

      // Aggregate
      let result: number;
      switch (driver.aggregation) {
        case DriverAggregation.Sum:
          result = inputValues.reduce((a, b) => a + b, 0);
          break;
        case DriverAggregation.Average:
          result = inputValues.reduce((a, b) => a + b, 0) / inputValues.length;
          break;
        case DriverAggregation.Min:
          result = Math.min(...inputValues);
          break;
        case DriverAggregation.Max:
          result = Math.max(...inputValues);
          break;
        default:
          result = inputValues[0];
      }

      // Apply FCurve mapping AFTER aggregation
      if (driver.fcurveMapping) {
        result = evaluateFCurve(driver.fcurveMapping, result);
      }

      // Write to target
      const targetWeights = weights.get(targetSetName);
      if (targetWeights) {
        targetWeights.set(targetKeyName, result);
        changes.push({
          setName: targetSetName,
          keyName: targetKeyName,
          value: result,
        });
      }
    }

    return changes;
  }

  /**
   * Returns the set of shape key names that are driven targets within a given set.
   *
   * Only keys that appear as targets in the resolved driver graph are included.
   * Drivers with no resolvable shape key source (e.g. bone transforms, LocDiff)
   * are excluded during build(), so they never appear here. This means primary
   * controller keys (whose only drivers are rig-level) remain editable, while
   * keys driven by other shape keys — whether cross-set or same-set — are
   * correctly disabled.
   */
  getDrivenKeysForSet(setName: string): Set<string> {
    const driven = new Set<string>();
    for (const node of this.nodes) {
      if (node.targetSetName === setName) {
        driven.add(node.targetKeyName);
      }
    }
    return driven;
  }

  get nodeCount(): number {
    return this.nodes.length;
  }
}

/**
 * Find which shape key set a datablock name belongs to.
 * Datablock names have a "KE" prefix in the .blend file.
 */
function findSetByDatablockName(
  sets: Map<string, BlendShapeKeySet>,
  datablockName: string,
): string | null {
  // Strip "KE" prefix if present
  const cleanName = datablockName.startsWith("KE")
    ? datablockName.slice(2)
    : datablockName;

  // Direct lookup
  if (sets.has(cleanName)) return cleanName;

  // Search by name
  for (const [name, _set] of sets) {
    if (name === cleanName || name === datablockName) return name;
  }

  return null;
}

/**
 * Topological sort using Kahn's algorithm.
 * Ensures drivers are evaluated after their dependencies.
 */
function topologicalSort(nodes: DriverNode[]): DriverNode[] {
  // Build adjacency: if node A's target is node B's source, A must come first
  // Key: "setName::keyName" for the target
  const targetKey = (n: DriverNode) => `${n.targetSetName}::${n.targetKeyName}`;
  const sourceKey = (s: { setName: string; keyName: string }) =>
    `${s.setName}::${s.keyName}`;

  // Map from target key to node index
  const targetToIdx = new Map<string, number>();
  for (let i = 0; i < nodes.length; i++) {
    targetToIdx.set(targetKey(nodes[i]), i);
  }

  // Build in-degree and adjacency
  const inDegree = new Array(nodes.length).fill(0);
  const adj = new Array(nodes.length).fill(null).map(() => [] as number[]);

  for (let i = 0; i < nodes.length; i++) {
    for (const src of nodes[i].sources) {
      const depIdx = targetToIdx.get(sourceKey(src));
      if (depIdx !== undefined && depIdx !== i) {
        adj[depIdx].push(i);
        inDegree[i]++;
      }
    }
  }

  // Kahn's algorithm
  const queue: number[] = [];
  for (let i = 0; i < nodes.length; i++) {
    if (inDegree[i] === 0) queue.push(i);
  }

  const sorted: DriverNode[] = [];
  while (queue.length > 0) {
    const idx = queue.shift()!;
    sorted.push(nodes[idx]);
    for (const next of adj[idx]) {
      inDegree[next]--;
      if (inDegree[next] === 0) queue.push(next);
    }
  }

  // If cycle detected, add remaining nodes anyway (shouldn't happen per spec)
  if (sorted.length < nodes.length) {
    const inSorted = new Set(sorted);
    for (let i = 0; i < nodes.length; i++) {
      if (!inSorted.has(nodes[i])) sorted.push(nodes[i]);
    }
  }

  return sorted;
}
